import React, { useEffect } from 'react';
import { Modal, Alert, Button, FormRow } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import { changeSalesUnitProject } from 'redux/crm/projectSlice';
import { SELECT_STYLES } from 'helpers/formStyles';

const buildGroupedOptions = (items) => {
  const groups = (items || []).reduce((result, item) => {
    const region = item.region || 'Other';
    if (!result[region]) result[region] = [];
    result[region].push({
      value: item.id,
      label: item.description ? `${item.name} — ${item.description}` : item.name,
    });
    return result;
  }, {});

  return Object.entries(groups)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([region, options]) => ({
      label: region.replaceAll('_', ' '),
      options: options.sort((left, right) => left.label.localeCompare(right.label)),
    }));
};

const ChangeSalesUnitForm = ({ showModal, setshowModal, project }) => {
  const { control, handleSubmit, formState: { errors, isSubmitting } } = useForm();
  const { activeSalesUnitsData } = useSelector(state => state.crm_sales_unit);
  const dispatch = useDispatch();

  const salesUnitOptions = buildGroupedOptions(activeSalesUnitsData);

  useEffect(() => {
    dispatch(getActiveSalesUnits());
  }, [dispatch]);

  const closeModal = () => setshowModal(false);

  const onSubmit = async (data) => {
    try {
      await dispatch(changeSalesUnitProject({ projectId: project.id, data })).unwrap();
      toast.success('Sales unit changed successfully.');
      closeModal();
    } catch (error) {
      toast.error(error?.message || 'Failed to change sales unit.');
    }
  };

  const footer = (
    <>
      <Button type="submit" size="sm" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
        {isSubmitting ? 'Changing…' : 'Change'}
      </Button>
      <Button variant="danger" size="sm" onClick={closeModal} disabled={isSubmitting}>Cancel</Button>
    </>
  );

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      title="Change sales unit!"
      backdropClick={false}
      footer={footer}
    >
      <Alert type="warning" className="tw-mb-4">
        All sales units for the project, building, job entry, change orders, and contracts will be updated
        to the new sales unit. Are you sure to change sales unit{' '}
        <strong>{project.sales_unit?.description}</strong>?
      </Alert>

      <FormRow label="To Sales unit" required error={errors.sales_unit_id?.message}>
        <Controller
          control={control}
          name="sales_unit_id"
          rules={{ required: 'Sales unit is required' }}
          render={({ field }) => (
            <Select
              closeMenuOnSelect
              options={salesUnitOptions}
              placeholder="Select..."
              menuPosition="fixed"
              styles={{
                ...SELECT_STYLES,
                groupHeading: (b) => ({
                  ...b,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: '#2563eb',
                  backgroundColor: '#eff6ff',
                  padding: '6px 12px',
                  marginBottom: 0,
                }),
                option: (b, s) => ({
                  ...SELECT_STYLES.option(b, s),
                  paddingLeft: 24,
                  paddingTop: 6,
                  paddingBottom: 6,
                  fontSize: 13,
                }),
              }}
              value={salesUnitOptions.flatMap(group => group.options).find(option => option.value === field.value) || null}
              onChange={option => field.onChange(option?.value || null)}
            />
          )}
        />
      </FormRow>
    </Modal>
  );
};

ChangeSalesUnitForm.propTypes = {
  showModal:    PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  project:      PropTypes.object.isRequired,
};

export default ChangeSalesUnitForm;
